import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import clsx from "clsx";
import { useTranslate } from "react-polyglot";
import { useHistory } from "react-router-dom";
import { makeStyles, Typography, Box, IconButton, Divider } from "@material-ui/core";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import chevronLeft from "../../../../assets/chevron-left.svg";
import useCompanyTitle from "../../../../hooks/useCompanyTitle.js";
import { useCourseProvider } from "../../../../Providers/Data/CourseProvider.jsx";
import FlexboxHorizontal from "../../../../SharedComponents/FlexboxHorizontal.jsx";
import LinearProgressBar from "../../../../SharedComponents/LinearProgressBar.jsx";

const useStyles = makeStyles((theme) => ({
  mainBox: {
    "& .MuiDivider-root": {
      margin: "20px 0",
    },
  },
  backButton: {
    width: 52,
    height: 44,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid #C5C7C9",
    borderRadius: 22,
    color: theme.palette.primary.main,
  },
  nameBox: {
    marginLeft: 24,
    padding: "10px 12px",
    cursor: "pointer",
  },
  nameBoxBefore: {
    position: "relative",
    "&::before": {
      content: "''",
      position: "absolute",
      left: -24,
      top: 25,
      width: 24,
      height: 24,
      backgroundImage: `url(${chevronLeft})`,
      backgroundSize: "contain",
      backgroundRepeat: "no-repeat",
    },
  },
  imageBox: {
    width: 54,
    height: 54,
    borderRadius: 12,
    overflow: "hidden",
    flexShrink: 0,
  },
  image: {
    width: "100%",
    height: "100%",
    display: "block",
    objectFit: "cover",
    objectPosition: "center",
  },
  itemTitle: {
    color: "#909293",
    fontSize: 11,
    paddingTop: 2,
  },
  itemName: {
    color: "#1B1C1C",
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.4,
    marginBottom: 2,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
  itemNameShort: {
    maxWidth: 256,
  },
  pointsTitle: {
    color: theme.palette.primary.main,
    fontSize: 12,
    marginLeft: 4,
  },
  itemProgress: {
    "& .MuiLinearProgress-bar": {
      borderRadius: 3,
    },
  },
}));

/** @param {Boolean} isLearnPage */
const LevelHeader = ({ isLearnPage }) => {
  const classes = useStyles();
  const t = useTranslate();
  useCompanyTitle("title.home");
  const { currentLevel, currentBranch, currentDeck } = useCourseProvider();
  const history = useHistory();
  const location = history.location.pathname;
  const [isDeckPage, setIsDeckPage] = useState(false);

  useEffect(() => {
    if (location === "/decks") {
      if (!currentBranch && currentLevel) {
        history.push("/branches");
      } else if (!currentBranch && !currentLevel) {
        history.push("/");
      } else {
        setIsDeckPage(true);
      }
    } else {
      setIsDeckPage(false);
    }
  }, [location, currentLevel, currentBranch]);

  /** */
  const onClickBack = () => {
    if (location.startsWith("/learn")) {
      history.push("/decks");
    } else if (location.startsWith("/decks")) {
      history.push("/branches");
    } else if (location.startsWith("/branches")) {
      history.push("/");
    }
  };
  /** */
  const onClickLevel = () => {
    history.push("/");
  };
  /** */
  const onClickBranch = () => {
    history.push("/branches");
  };
  /** */
  const onClickDeck = () => {
    history.push("/decks");
  };

  if (!currentLevel) return null;

  return (
    <Box className={classes.mainBox}>
      <FlexboxHorizontal alignItems="center">
        <IconButton aria-label="close" className={classes.backButton} onClick={onClickBack}>
          <ChevronLeftIcon />
        </IconButton>
        <FlexboxHorizontal className={classes.nameBox} onClick={onClickLevel}>
          {currentLevel?.image?.url && (
            <Box className={classes.imageBox}>
              <img className={classes.image} src={currentLevel.image.url} alt="image of level" />
            </Box>
          )}
          <Box ml={1}>
            <Typography className={classes.itemTitle}>{t("Levels.name")}</Typography>
            <Typography className={clsx(classes.itemName, { [classes.itemNameShort]: isDeckPage })}>
              {currentLevel.name}
            </Typography>
            <FlexboxHorizontal alignItems="center">
              <LinearProgressBar
                className={classes.itemProgress}
                variant="determinate"
                value={(currentLevel.havePoints / currentLevel.points) * 100}
                height={6}
                color="primary"
                style={{
                  borderRadius: 3,
                  width: 80,
                }}
                noborder
                resetBackgroundColor
              />
              <Typography className={classes.pointsTitle} color="primary">
                {`${Math.round((currentLevel.havePoints / currentLevel.points) * 100)}%`}
              </Typography>
            </FlexboxHorizontal>
          </Box>
        </FlexboxHorizontal>
        {currentBranch && (isDeckPage || isLearnPage) && (
          <FlexboxHorizontal className={clsx(classes.nameBox, classes.nameBoxBefore)} onClick={onClickBranch}>
            {(currentBranch.illustrationImage?.url || currentBranch.illustrationImageUrl) && (
              <Box className={classes.imageBox}>
                <img
                  className={classes.image}
                  src={currentBranch.illustrationImage?.url || currentBranch.illustrationImageUrl}
                  alt="image of topick"
                />
              </Box>
            )}
            <Box ml={1}>
              <Typography className={classes.itemTitle}>{`${t("topic")}:`}</Typography>
              <Typography className={classes.itemName}>{currentBranch.name}</Typography>
              <FlexboxHorizontal alignItems="center">
                <LinearProgressBar
                  className={classes.itemProgress}
                  variant="determinate"
                  value={currentBranch.percent}
                  height={6}
                  color="primary"
                  style={{
                    borderRadius: 3,
                    width: 80,
                  }}
                  noborder
                  resetBackgroundColor
                />
                <Typography className={classes.pointsTitle} color="primary">
                  {`${Math.round(currentBranch.percent)}%`}
                </Typography>
              </FlexboxHorizontal>
            </Box>
          </FlexboxHorizontal>
        )}
        {isLearnPage && currentDeck && (
          <FlexboxHorizontal className={clsx(classes.nameBox, classes.nameBoxBefore)} onClick={onClickDeck}>
            {(currentDeck.illustrationImage?.url || currentDeck.illustrationImageUrl) && (
              <Box className={classes.imageBox}>
                <img
                  className={classes.image}
                  src={currentDeck.illustrationImage?.url || currentDeck.illustrationImageUrl}
                  alt="image of lesson"
                />
              </Box>
            )}
            <Box ml={1}>
              <Typography className={classes.itemTitle}>{`${t("lesson")}:`}</Typography>
              <Typography className={classes.itemName}>{currentDeck.name}</Typography>
              <FlexboxHorizontal alignItems="center">
                <LinearProgressBar
                  className={classes.itemProgress}
                  variant="determinate"
                  value={currentDeck.percent}
                  height={6}
                  color="primary"
                  style={{
                    borderRadius: 3,
                    width: 80,
                  }}
                  noborder
                  resetBackgroundColor
                />
                <Typography className={classes.pointsTitle} color="primary">
                  {`${Math.round(currentDeck.percent)}%`}
                </Typography>
              </FlexboxHorizontal>
            </Box>
          </FlexboxHorizontal>
        )}
      </FlexboxHorizontal>
      <Divider />
    </Box>
  );
};

LevelHeader.propTypes = {
  isLearnPage: PropTypes.bool,
};

export default LevelHeader;
